import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { geoAlbersUsa } from 'd3-geo';
import { MapPin, RefreshCw, Download, Search, AlertTriangle, UserCheck, UserX, Building2 } from 'lucide-react';

const US_TOPO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';
const PLACEMENTS_URL = '/.netlify/functions/fe-placements';

const GREEN = '#4ADE80';
const RED = '#F87171';

// Individual school pins only make sense once you're zoomed in; at national scale 1,400
// dots stack into noise. Below these zoom levels markers are gridded into clusters.
const CLUSTER_LEVELS = [
  { maxZoom: 2.5, grid: 1.0 },
  { maxZoom: 5, grid: 0.35 },
];

// geoAlbersUsa returns null for anything outside its US envelope, and react-simple-maps
// destructures that result without checking — one bad coordinate blanks the whole page.
// Screen points through the projection before handing them to <Marker>.
const PROJECTION = geoAlbersUsa();
const isProjectable = (p) => PROJECTION([p.lng, p.lat]) !== null;

// Pie wedge from 12 o'clock, sweeping clockwise. Used to show what share of a cluster
// still needs a supervisor, so a mostly-covered cluster doesn't read as a solid red gap.
const wedgePath = (r, fraction) => {
  if (fraction <= 0) return null;
  if (fraction >= 1) {
    return `M 0 ${-r} A ${r} ${r} 0 1 1 0 ${r} A ${r} ${r} 0 1 1 0 ${-r} Z`;
  }
  const angle = fraction * Math.PI * 2;
  const x = r * Math.sin(angle);
  const y = -r * Math.cos(angle);
  return `M 0 0 L 0 ${-r} A ${r} ${r} 0 ${angle > Math.PI ? 1 : 0} 1 ${x} ${y} Z`;
};

const gridFor = (zoom) => {
  const level = CLUSTER_LEVELS.find((l) => zoom < l.maxZoom);
  return level ? level.grid : null;
};

const PlacementMap = ({ semester = 'Fall 2026' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapZoom, setMapZoom] = useState(1);
  const [mapCenter, setMapCenter] = useState([-96, 38]);
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const [coverageFilter, setCoverageFilter] = useState('all'); // all | unassigned | assigned
  const [stateFilter, setStateFilter] = useState('All');
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${PLACEMENTS_URL}?semester=${encodeURIComponent(semester)}`);
      if (!res.ok) throw new Error(`Placements fetch failed: ${res.status}`);
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [semester]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // A marker removed by a filter or zoom change never fires onMouseLeave, which strands
  // its tooltip on screen describing a school that is no longer drawn.
  useEffect(() => {
    setHovered(null);
    setSelected(null);
  }, [stateFilter, coverageFilter, search, mapZoom]);

  const states = useMemo(() => {
    if (!data) return [];
    return ['All', ...data.byState.map((s) => s.state)];
  }, [data]);

  // Points the projection can actually place. Anything it rejects is still counted in the
  // totals and the export; it just can't be drawn.
  const mappablePoints = useMemo(() => (data ? data.points.filter(isProjectable) : []), [data]);

  const offMapCount = useMemo(() => {
    if (!data) return 0;
    const drawn = new Set(mappablePoints.map((p) => p.id));
    return data.points.filter((p) => !drawn.has(p.id)).reduce((s, p) => s + p.total, 0);
  }, [data, mappablePoints]);

  // Points surviving the filter bar. Search matches school, district or supervisor so the
  // same box works for "who covers Katy ISD" and "where is Tiffany Reinhart placed".
  const filteredPoints = useMemo(() => {
    const q = search.trim().toLowerCase();
    return mappablePoints.filter((p) => {
      if (stateFilter !== 'All' && p.state !== stateFilter) return false;
      if (coverageFilter === 'unassigned' && p.unassigned === 0) return false;
      if (coverageFilter === 'assigned' && p.unassigned > 0) return false;
      if (q) {
        const hay = [p.school, p.district, ...p.placements.map((x) => x.supervisor || ''), ...p.placements.map((x) => x.candidate)]
          .join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [mappablePoints, stateFilter, coverageFilter, search]);

  // Cluster the filtered points onto a lat/lng grid when zoomed out.
  const markers = useMemo(() => {
    const grid = gridFor(mapZoom);
    if (!grid) {
      return filteredPoints.map((p) => ({
        id: p.id,
        lat: p.lat,
        lng: p.lng,
        total: p.total,
        assigned: p.assigned,
        unassigned: p.unassigned,
        schools: [p],
        isCluster: false,
      }));
    }
    const buckets = new Map();
    for (const p of filteredPoints) {
      const gk = `${Math.round(p.lat / grid)}|${Math.round(p.lng / grid)}`;
      if (!buckets.has(gk)) {
        buckets.set(gk, { id: gk, latSum: 0, lngSum: 0, total: 0, assigned: 0, unassigned: 0, schools: [] });
      }
      const b = buckets.get(gk);
      b.latSum += p.lat; b.lngSum += p.lng;
      b.total += p.total; b.assigned += p.assigned; b.unassigned += p.unassigned;
      b.schools.push(p);
    }
    return Array.from(buckets.values()).map((b) => ({
      id: b.id,
      lat: b.latSum / b.schools.length,
      lng: b.lngSum / b.schools.length,
      total: b.total,
      assigned: b.assigned,
      unassigned: b.unassigned,
      schools: b.schools,
      isCluster: b.schools.length > 1,
    }));
  }, [filteredPoints, mapZoom]);

  const maxTotal = useMemo(() => Math.max(1, ...markers.map((m) => m.total)), [markers]);

  // Counts reflect the current filter so the header always describes what's on screen.
  const visible = useMemo(() => {
    const total = filteredPoints.reduce((s, p) => s + p.total, 0);
    const unassigned = filteredPoints.reduce((s, p) => s + p.unassigned, 0);
    return { total, unassigned, assigned: total - unassigned, schools: filteredPoints.length };
  }, [filteredPoints]);

  const exportUnassigned = useCallback(() => {
    if (!data) return;
    const rows = [['Candidate', 'State', 'School', 'District', 'Address', 'Mentor Teacher', 'FE Semester']];
    for (const p of data.points) {
      for (const pl of p.placements) {
        if (pl.assigned) continue;
        rows.push([pl.candidate, p.state, p.school, p.district || '', p.address, pl.mentor || '', data.semester]);
      }
    }
    for (const u of data.unplaced) {
      if (u.assigned) continue;
      rows.push([u.candidate, u.state, u.school || '', u.district || '', u.address || '(no address)', u.mentor || '', data.semester]);
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `unassigned-supervisors-${data.semester.replace(/\s+/g, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [data]);

  const handleMove = useCallback(({ coordinates, zoom }) => {
    setMapCenter(coordinates);
    setMapZoom(zoom);
  }, []);

  if (loading) {
    return (
      <div className="pm-loading">
        <RefreshCw size={20} className="pm-spin" />
        <span>Loading {semester} placements…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pm-error">
        <AlertTriangle size={18} />
        <div>
          <div className="pm-error-title">Couldn't load placements</div>
          <div className="pm-error-msg">{error}</div>
        </div>
        <button className="pm-btn" onClick={fetchData}>Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const s = data.summary;

  return (
    <div className="pm-root">
      {/* KPIs */}
      <div className="hf-kpi-row">
        <div className="hf-kpi">
          <div className="hf-kpi-value">{s.total.toLocaleString()}</div>
          <div className="hf-kpi-label">{data.semester} Placements</div>
          <div className="hf-kpi-sub">{s.schools.toLocaleString()} schools · {s.districts.toLocaleString()} districts</div>
        </div>
        <div className="hf-kpi" style={{ border: `1px solid ${GREEN}44` }}>
          <div className="hf-kpi-value" style={{ color: GREEN }}>{s.coveragePct}%</div>
          <div className="hf-kpi-label">Supervisor Coverage</div>
          <div className="hf-kpi-sub">{s.assigned.toLocaleString()} of {s.total.toLocaleString()} assigned</div>
        </div>
        <div className="hf-kpi" style={{ border: `1px solid ${RED}44` }}>
          <div className="hf-kpi-value" style={{ color: RED }}>{s.unassigned.toLocaleString()}</div>
          <div className="hf-kpi-label">Awaiting a Supervisor</div>
          <div className="hf-kpi-sub">
            {data.byState.filter((x) => x.unassigned > 0).length} states affected
          </div>
        </div>
        <div className="hf-kpi">
          <div className="hf-kpi-value">{s.supervisors.toLocaleString()}</div>
          <div className="hf-kpi-label">Active Supervisors</div>
          <div className="hf-kpi-sub">
            median {s.medianCaseload} candidates
            {s.overCapacity > 0 && <span style={{ color: '#FBBF24' }}> · {s.overCapacity} over capacity</span>}
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="hf-map-card">
        <div className="hf-map-header">
          <h3 className="hf-section-title">
            <MapPin size={18} />
            {data.semester} Placements &amp; Supervisor Assignment
          </h3>
          <div className="pm-controls">
            <div className="pm-search">
              <Search size={13} />
              <input
                type="text"
                placeholder="School, district, or supervisor…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select className="hf-state-dropdown" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
              {states.map((st) => (
                <option key={st} value={st}>
                  {st === 'All' ? `All states (${s.total})` : `${st} (${data.byState.find((x) => x.state === st)?.total || 0})`}
                </option>
              ))}
            </select>
            <div className="pm-toggle">
              {[
                { id: 'all', label: 'All' },
                { id: 'unassigned', label: 'Gaps only' },
                { id: 'assigned', label: 'Covered' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  className={`pm-toggle-btn ${coverageFilter === opt.id ? 'active' : ''}`}
                  onClick={() => setCoverageFilter(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button className="pm-btn" onClick={exportUnassigned} title="Download the unassigned list for outreach">
              <Download size={13} /> Export gaps
            </button>
          </div>
        </div>

        <div className="pm-legend">
          <span className="pm-legend-item"><span className="pm-dot" style={{ background: GREEN }} /> Supervisor assigned</span>
          <span className="pm-legend-item"><span className="pm-dot" style={{ background: RED }} /> Needs a supervisor</span>
          {gridFor(mapZoom) && (
            <span className="pm-legend-item pm-muted">
              <svg width="15" height="15" viewBox="-8 -8 16 16" aria-hidden="true">
                <circle r="7" fill={`${GREEN}CC`} />
                <path d={wedgePath(7, 0.3)} fill={`${RED}E6`} />
                <circle r="4.3" fill="rgba(15, 23, 42, 0.92)" />
              </svg>
              Grouped schools: red slice and number show how many need a supervisor
            </span>
          )}
          <span className="pm-legend-sep" />
          <span className="pm-legend-note">
            Showing {visible.schools.toLocaleString()} schools · {visible.total.toLocaleString()} placements
            {visible.unassigned > 0 && <strong style={{ color: RED }}> · {visible.unassigned.toLocaleString()} unassigned</strong>}
          </span>
          {gridFor(mapZoom) && <span className="pm-legend-note pm-muted">Zoom in for individual schools</span>}
        </div>

        <div className="hf-map-container" style={{ position: 'relative' }}>
          {mapZoom > 1 && (
            <button className="hf-zoom-out-btn" onClick={() => { setMapZoom(1); setMapCenter([-96, 38]); setSelected(null); }}>
              ← All States
            </button>
          )}
          <ComposableMap
            projection="geoAlbersUsa"
            projectionConfig={{ scale: 900 }}
            width={800}
            height={520}
            style={{ width: '100%', height: 'auto', maxHeight: '700px', background: 'rgba(15, 23, 42, 0.6)', borderRadius: '8px' }}
          >
            <ZoomableGroup center={mapCenter} zoom={mapZoom} minZoom={1} maxZoom={14} onMoveEnd={handleMove}>
              <Geographies geography={US_TOPO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey || geo.id}
                      geography={geo}
                      style={{
                        default: { fill: 'rgba(30, 41, 59, 0.85)', stroke: 'rgba(199, 210, 254, 0.15)', strokeWidth: 0.5, outline: 'none' },
                        hover: { fill: 'rgba(30, 41, 59, 0.85)', stroke: 'rgba(199, 210, 254, 0.25)', strokeWidth: 0.6, outline: 'none' },
                        pressed: { outline: 'none' },
                      }}
                    />
                  ))
                }
              </Geographies>

              {markers.map((m) => {
                const hasGap = m.unassigned > 0;
                const gapShare = m.total ? m.unassigned / m.total : 0;
                const colour = hasGap ? RED : GREEN;
                const intensity = m.total / maxTotal;
                const base = m.isCluster ? Math.max(5, Math.min(22, 5 + intensity * 17)) : Math.max(3.5, Math.min(11, 3.5 + intensity * 7));
                const r = base / Math.pow(mapZoom, 0.35);
                const isHovered = hovered && hovered.id === m.id;
                // The number only ever means "needs a supervisor" — a fully covered cluster
                // carries no label, so a count on the map is always actionable.
                const showLabel = m.isCluster && hasGap && r > 9;
                return (
                  <Marker key={m.id} coordinates={[m.lng, m.lat]}>
                    <g
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => {
                        const rect = e.target.closest('svg').getBoundingClientRect();
                        setHovered({ ...m, screenX: e.clientX - rect.left, screenY: e.clientY - rect.top });
                      }}
                      onMouseMove={(e) => {
                        const rect = e.target.closest('svg').getBoundingClientRect();
                        setHovered((prev) => (prev && prev.id === m.id ? { ...prev, screenX: e.clientX - rect.left, screenY: e.clientY - rect.top } : prev));
                      }}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => {
                        if (m.isCluster) {
                          setMapCenter([m.lng, m.lat]);
                          setMapZoom((z) => Math.min(14, z * 3));
                        } else {
                          setSelected(m.schools[0]);
                        }
                      }}
                    >
                      {/* Halo intensity tracks the size of the gap, not merely its existence */}
                      {hasGap && <circle r={r * 1.9} fill={`${RED}${gapShare > 0.5 ? '26' : '14'}`} />}

                      {m.isCluster ? (
                        <>
                          {/* Green base with a red wedge sized to the unassigned share, so
                              61-of-68-covered looks mostly covered rather than all red. */}
                          <circle r={r} fill={`${GREEN}CC`} stroke={GREEN} strokeWidth={0.6} />
                          {hasGap && <path d={wedgePath(r, gapShare)} fill={`${RED}E6`} />}
                          {isHovered && <circle r={r} fill="none" stroke="#fff" strokeWidth={1.4} />}
                          {showLabel && (
                            <>
                              {/* Donut hole keeps the count legible over either colour */}
                              <circle r={r * 0.62} fill="rgba(15, 23, 42, 0.92)" />
                              <text
                                textAnchor="middle"
                                y={r * 0.26}
                                style={{ fontSize: r * 0.72, fill: RED, fontWeight: 700, pointerEvents: 'none' }}
                              >
                                {m.unassigned}
                              </text>
                            </>
                          )}
                        </>
                      ) : (
                        <circle
                          r={r}
                          fill={isHovered ? colour : `${colour}CC`}
                          stroke={isHovered ? '#fff' : colour}
                          strokeWidth={isHovered ? 1.4 : 0.6}
                        />
                      )}
                    </g>
                  </Marker>
                );
              })}
            </ZoomableGroup>
          </ComposableMap>

          {hovered && (
            <div
              className="hf-map-tooltip pm-tooltip"
              style={{
                left: Math.min(hovered.screenX + 14, 620),
                top: Math.max(hovered.screenY - 10, 8),
              }}
            >
              {hovered.isCluster ? (
                <>
                  <div className="hf-tooltip-city">{hovered.schools.length} schools</div>
                  <div className="hf-tooltip-count">{hovered.total} placements</div>
                  <div className="pm-tt-split">
                    <span style={{ color: GREEN }}>{hovered.assigned} assigned</span>
                    {hovered.unassigned > 0 && <span style={{ color: RED }}>{hovered.unassigned} unassigned</span>}
                  </div>
                  <div className="hf-tooltip-action">Click to zoom in</div>
                </>
              ) : (
                <>
                  <div className="hf-tooltip-city">{hovered.schools[0].school}</div>
                  {hovered.schools[0].district && <div className="hf-tooltip-sub">{hovered.schools[0].district}</div>}
                  <div className="hf-tooltip-sub pm-muted">{hovered.schools[0].address}</div>
                  <div className="pm-tt-list">
                    {hovered.schools[0].placements.slice(0, 6).map((p, i) => (
                      <div key={i} className="pm-tt-row">
                        <span className="pm-dot" style={{ background: p.assigned ? GREEN : RED }} />
                        <span className="pm-tt-cand">{p.candidate}</span>
                        <span className="pm-tt-sup" style={{ color: p.assigned ? GREEN : RED }}>
                          {p.assigned ? p.supervisor : 'No supervisor'}
                        </span>
                      </div>
                    ))}
                    {hovered.schools[0].placements.length > 6 && (
                      <div className="pm-tt-more">+{hovered.schools[0].placements.length - 6} more</div>
                    )}
                  </div>
                  {hovered.schools[0].precision === 'zip3' && (
                    <div className="hf-tooltip-action pm-muted">Approx. location (ZIP prefix)</div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {selected && (
          <div className="pm-detail">
            <div className="pm-detail-head">
              <div>
                <div className="pm-detail-title"><Building2 size={14} /> {selected.school}</div>
                <div className="pm-detail-sub">{selected.district || '—'} · {selected.address}</div>
              </div>
              <button className="pm-btn pm-btn-ghost" onClick={() => setSelected(null)}>Close</button>
            </div>
            <div className="pm-detail-grid">
              {selected.placements.map((p, i) => (
                <div key={i} className="pm-detail-row">
                  <span className="pm-dot" style={{ background: p.assigned ? GREEN : RED }} />
                  <span className="pm-detail-cand">{p.candidate}</span>
                  <span className="pm-detail-sup">
                    {p.assigned ? (
                      <><UserCheck size={12} style={{ color: GREEN }} /> {p.supervisor}</>
                    ) : (
                      <><UserX size={12} style={{ color: RED }} /> Needs assignment</>
                    )}
                  </span>
                  <span className="pm-detail-mentor">{p.mentor ? `MT: ${p.mentor}` : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recruitment intelligence */}
      <div className="pm-panels">
        <div className="hf-map-card pm-panel">
          <h3 className="hf-section-title"><AlertTriangle size={16} /> Where Supervisors Are Needed</h3>
          <div className="pm-panel-sub">States ranked by unassigned placements</div>
          <div className="pm-table">
            {data.byState.map((st) => {
              const pct = st.total ? (st.assigned / st.total) * 100 : 0;
              return (
                <button
                  key={st.state}
                  className={`pm-row ${stateFilter === st.state ? 'active' : ''}`}
                  onClick={() => { setStateFilter(stateFilter === st.state ? 'All' : st.state); setCoverageFilter('unassigned'); }}
                >
                  <span className="pm-row-name">{st.state}</span>
                  <span className="pm-row-bar">
                    <span className="pm-row-fill" style={{ width: `${pct}%`, background: pct === 100 ? GREEN : pct > 60 ? '#FBBF24' : RED }} />
                  </span>
                  <span className="pm-row-pct">{pct.toFixed(0)}%</span>
                  <span className="pm-row-gap" style={{ color: st.unassigned ? RED : 'rgba(199,210,254,0.4)' }}>
                    {st.unassigned || '—'}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="pm-panel-foot">Click a state to filter the map to its gaps.</div>
        </div>

        <div className="hf-map-card pm-panel">
          <h3 className="hf-section-title"><Building2 size={16} /> District Hot Spots</h3>
          <div className="pm-panel-sub">Districts with the most unassigned candidates — best targets for a single recruit</div>
          <div className="pm-table">
            {data.byDistrict.length === 0 && <div className="pm-empty">No unassigned placements. 🎉</div>}
            {data.byDistrict.map((d) => (
              <div key={`${d.district}|${d.state}`} className="pm-row pm-row-static">
                <span className="pm-row-name">
                  {d.district}
                  <span className="pm-row-meta">{d.state}</span>
                </span>
                <span className="pm-row-gap" style={{ color: RED }}>{d.unassigned}</span>
                <span className="pm-row-total">of {d.total}</span>
              </div>
            ))}
          </div>
          <div className="pm-panel-foot">
            One supervisor placed in a hot-spot district can clear several assignments at once.
          </div>
        </div>

        <div className="hf-map-card pm-panel">
          <h3 className="hf-section-title"><UserCheck size={16} /> Supervisor Caseload</h3>
          <div className="pm-panel-sub">
            {s.supervisors} supervisors · median {s.medianCaseload} · {s.overCapacity} carrying more than 15
          </div>
          <div className="pm-table">
            {data.supervisors.slice(0, 20).map((sup) => (
              <button
                key={sup.name}
                className="pm-row"
                onClick={() => { setSearch(sup.name); setCoverageFilter('all'); setStateFilter('All'); }}
                title="Show this supervisor's schools on the map"
              >
                <span className="pm-row-name">
                  {sup.name}
                  <span className="pm-row-meta">{sup.states.join(', ')} · {sup.districts} districts</span>
                </span>
                <span className="pm-row-bar">
                  <span
                    className="pm-row-fill"
                    style={{
                      width: `${Math.min(100, (sup.count / (data.supervisors[0]?.count || 1)) * 100)}%`,
                      background: sup.overCapacity ? '#FBBF24' : 'rgba(96, 165, 250, 0.75)',
                    }}
                  />
                </span>
                <span className="pm-row-gap" style={{ color: sup.overCapacity ? '#FBBF24' : 'rgba(199,210,254,0.75)' }}>
                  {sup.count}
                </span>
              </button>
            ))}
          </div>
          <div className="pm-panel-foot">Click a supervisor to see their placements on the map.</div>
        </div>
      </div>

      {/* Data caveats — kept visible so nothing is silently dropped */}
      <div className="pm-caveats">
        <AlertTriangle size={13} />
        <span>
          <strong>{data.excludedStates.join(', ')}</strong> excluded — their FE records carry no school address
          {Object.keys(data.excludedCounts).length > 0 && (
            <> ({Object.entries(data.excludedCounts).map(([k, v]) => `${k} ${v}`).join(', ')})</>
          )}.
          {s.unplaced + offMapCount > 0 && (
            <> {(s.unplaced + offMapCount).toLocaleString()} further record{s.unplaced + offMapCount === 1 ? '' : 's'} could not be placed on the map (missing or unrecognised address) but {s.unplaced + offMapCount === 1 ? 'is' : 'are'} counted in the totals above and included in the export.</>
          )}
          {' '}Includes every {data.semester} field experience regardless of Date Entered.
        </span>
      </div>
    </div>
  );
};

export default PlacementMap;
